import { HubFeature } from '@ats/api/cm';
import {
	LocDef,
	LocDefs,
	LocMap,
	SPCredentialAgreementLoc,
	spCredentialAgreementLoc,
} from '@cm/core/localization';

const sharedBase: Pick<LocDef, 'shared' | 'htmlAllowed' | 'portal'> = {
	shared: false,
	htmlAllowed: false,
	portal: 'SP',
};

const sharedProperties: Pick<LocDef, 'shared' | 'htmlAllowed' | 'feature' | 'portal'> = {
	feature: HubFeature.OtherRequirement,
	...sharedBase,
};

const basePath = 'Catalogs|Other Requirement|Details|';

export type SPOtherRequirementDetailsLocNames =
	| 'SPOtherRequirementDetailsPageTitle'
	| 'SPOtherRequirementDetailsSectionTitle'
	| 'SPOtherRequirementDetailsButtonEdit'
	| 'SPOtherRequirementDetailsButtonCancel'
	| 'SPOtherRequirementDetailsButtonUpdate'
	| 'SPOtherRequirementDetailsButtonLatestAgreement'
	| 'SPOtherRequirementDetailsFieldLabelGroup'
	| 'SPOtherRequirementDetailsFieldLabelName'
	| 'SPOtherRequirementDetailsFieldLabelType'
	| 'SPOtherRequirementDetailsFieldLabelValidPeriod'
	| 'SPOtherRequirementDetailsFieldLabelCADCExclude'
	| 'SPOtherRequirementDetailsFieldLabelSingleUse'
	| 'SPOtherRequirementDetailsFieldLabelCreateType'
	| 'SPOtherRequirementDetailsFieldLabelDocRequired'
	| 'SPOtherRequirementDetailsFieldLabelStartDate'
	| 'SPOtherRequirementDetailsFieldLabelEndDate'
	| 'SPOtherRequirementDetailsFieldLabelMaxPoints'
	| 'SPOtherRequirementDetailsFieldLabelPoints'
	| 'SPOtherRequirementDetailsFieldLabelFactorUnits'
	| 'SPOtherRequirementDetailsFieldLabelDisplayOrder'
	| 'SPOtherRequirementDetailsFieldLabelExternalId'
	| 'SPOtherRequirementDetailsFieldLabelCreateAge'
	| 'SPOtherRequirementDetailsFieldLabelUrl'
	| 'SPOtherRequirementDetailsFieldLabelSchedulingUrl'
	| 'SPOtherRequirementDetailsFieldLabelTranscriptEnabled'
	| 'SPOtherRequirementDetailsFieldLabelProviderRequired'
	| 'SPOtherRequirementDetailsFieldLabelDescription'
	| 'SPOtherRequirementDetailsViewLatestAgreement'
	| 'SPOtherRequirementDetailsContentValidForever'
	| 'SPOtherRequirementDetailsContentDocNotAllowed'
	| 'SPOtherRequirementDetailsContentDocAllowed'
	| 'SPOtherRequirementDetailsContentDocRequired'
	| 'SPOtherRequirementDetailsContinuingEducationMaxPoints'
	| 'SPOtherRequirementDetailsContinuingEducationPoints'
	| 'SPOtherRequirementDetailsContinuingEducationFactorUnits'
	| 'SPOtherRequirementDetailsContinuingEducationFactor'
	| 'SPOtherRequirementDetailsContentNoLimitText'
	| 'SPOtherRequirementDetailsContentProviderNotAllowed'
	| 'SPOtherRequirementDetailsContentProviderAllowed'
	| 'SPOtherRequirementDetailsContentProviderRequired'
	| 'SPOtherRequirementDetailsFieldErrorNameRequired'
	| 'SPOtherRequirementDetailsFieldErrorDisplayOrderInvalid'
	| 'SPOtherRequirementDetailsDateRangeInvalid'
	| 'SPOtherRequirementDetailsInformationUrlFieldLabelUrl'
	| 'SPOtherRequirementDetailsInformationUrlFieldErrorUrlInvalid'
	| 'SPOtherRequirementDetailsSchedulingUrlFieldLabelSchedulingUrl'
	| 'SPOtherRequirementDetailsSchedulingUrlFieldErrorSchedulingUrlInvalid'
	| 'SPOtherRequirementDetailsSaveSuccess'
	| 'SPOtherRequirementDetailsContentCreateAgeTooltip';

export const spOtherRequirementDetailsLocs: LocDefs<
	SPOtherRequirementDetailsLocNames & SPCredentialAgreementLoc
> = {
	...spCredentialAgreementLoc,
	SPOtherRequirementDetailsContentCreateAgeTooltip: {
		defaultValue:
			'This is the maximum age allowed to be used for the active-date when a candidate creates a new certification requirement (e.g. via the web). Leave empty to have no limit imposed. Otherwise, you must enter the active-date. The minimum date you are allowed to enter is this many months in the past. If this is zero, the active-date is the date of creation; the candidate cannot set it.',
		description: 'Tooltip text for the create age field.',
		displayPath: `${basePath}Create Age Tooltip`,
		displayType: 'Tooltip',
		legacyName: 'OtherRequirement/Catalogs.OtherRequirement.Details.Content.CreateAgeTooltip',
		...sharedProperties,
	},
	SPOtherRequirementDetailsSaveSuccess: {
		defaultValue: 'Changes saved',
		description: 'Notification message displayed when changes are saved.',
		displayPath: `${basePath}Save Success`,
		displayType: 'Notification',
		legacyName:
			'OtherRequirement/Catalogs.OtherRequirement.Details.NotificationMessage.Success',
		...sharedProperties,
	},
	SPOtherRequirementDetailsPageTitle: {
		defaultValue: 'Other Requirement Details',
		description: 'Tab title for the other requirements details page.',
		displayPath: `${basePath}Page Title`,
		displayType: 'Title',
		legacyName: 'OtherRequirement/Catalogs.OtherRequirement.Details.Title.Tab',
		...sharedProperties,
	},
	SPOtherRequirementDetailsSectionTitle: {
		defaultValue: 'Details',
		description: 'The details section title for the other requirements details page.',
		displayPath: `${basePath}Section Title`,
		displayType: 'Title',
		legacyName: 'OtherRequirement/Catalogs.OtherRequirement.Details.Title.Details',
		...sharedProperties,
	},
	SPOtherRequirementDetailsButtonEdit: {
		defaultValue: 'Edit',
		description: 'The edit button label for the other requirements details page.',
		displayPath: `${basePath}Edit`,
		displayType: 'Button',
		legacyName: 'OtherRequirement/Catalogs.OtherRequirement.Details.Button.Edit',
		...sharedProperties,
	},
	SPOtherRequirementDetailsButtonCancel: {
		defaultValue: 'Cancel',
		description: 'The cancel button label for the other requirements details page.',
		displayPath: `${basePath}Cancel`,
		displayType: 'Button',
		legacyName: 'OtherRequirement/Catalogs.OtherRequirement.Details.Button.Cancel',
		...sharedProperties,
	},
	SPOtherRequirementDetailsButtonUpdate: {
		defaultValue: 'Update',
		description: 'The update button label for the other requirements details page.',
		displayPath: `${basePath}Update`,
		displayType: 'Button',
		legacyName: 'OtherRequirement/Catalogs.OtherRequirement.Details.Button.Update',
		...sharedProperties,
	},
	SPOtherRequirementDetailsButtonLatestAgreement: {
		defaultValue: 'View/Edit Latest Agreement',
		description: 'The latest agreement button label for the other requirements details page.',
		displayPath: `${basePath}Latest Agreement`,
		displayType: 'Button',
		legacyName: 'Credential.Agreement/Catalogs.OtherRequirement.Details.Button.LatestAgreement',
		...sharedBase,
		feature: HubFeature.CredentialAgreement,
	},
	SPOtherRequirementDetailsFieldLabelGroup: {
		defaultValue: 'Group',
		description: 'The label for the Group field in the other requirements details page.',
		displayPath: `${basePath}Group`,
		displayType: 'Label',
		legacyName: 'OtherRequirement/Catalogs.OtherRequirement.Details.Label.Group',
		...sharedProperties,
	},
	SPOtherRequirementDetailsFieldLabelName: {
		defaultValue: 'Name',
		description: 'The label for the Name field in the other requirements details page.',
		displayPath: `${basePath}Name`,
		displayType: 'Label',
		legacyName: 'OtherRequirement/Catalogs.OtherRequirement.Details.Label.Name',
		...sharedProperties,
	},
	SPOtherRequirementDetailsFieldLabelType: {
		defaultValue: 'Type',
		description: 'The label for the Type field in the other requirements details page.',
		displayPath: `${basePath}Type`,
		displayType: 'Label',
		legacyName: 'OtherRequirement/Catalogs.OtherRequirement.Details.Label.Type',
		...sharedProperties,
	},
	SPOtherRequirementDetailsFieldLabelValidPeriod: {
		defaultValue: 'Valid (In Months)',
		description: 'The label for the Valid Period field in the other requirements details page.',
		displayPath: `${basePath}Valid Period`,
		displayType: 'Label',
		legacyName: 'OtherRequirement/Catalogs.OtherRequirement.Details.Label.ValidPeriod',
		...sharedProperties,
	},
	SPOtherRequirementDetailsFieldLabelCADCExclude: {
		defaultValue: 'CADC Exclude',
		description: 'The label for the CADCExclude field in the other requirements details page.',
		displayPath: `${basePath}CADC Exclude`,
		displayType: 'Label',
		legacyName: 'OtherRequirement/Catalogs.OtherRequirement.Details.Label.CADCExclude',
		...sharedProperties,
	},
	SPOtherRequirementDetailsFieldLabelSingleUse: {
		defaultValue: 'Single Use',
		description: 'The label for the Single Use field in the other requirements details page.',
		displayPath: `${basePath}Single Use`,
		displayType: 'Label',
		legacyName: 'OtherRequirement/Catalogs.OtherRequirement.Details.Label.SingleUse',
		...sharedProperties,
	},
	SPOtherRequirementDetailsFieldLabelCreateType: {
		defaultValue: 'Create Type',
		description: 'The label for the Create Type field in the other requirements details page.',
		displayPath: `${basePath}Create Type`,
		displayType: 'Label',
		legacyName: 'OtherRequirement/Catalogs.OtherRequirement.Details.Label.CreateType',
		...sharedProperties,
	},
	SPOtherRequirementDetailsFieldLabelDocRequired: {
		defaultValue: 'Doc Required',
		description: 'The label for the Doc Required field in the other requirements details page.',
		displayPath: `${basePath}Doc Required`,
		displayType: 'Label',
		legacyName: 'OtherRequirement/Catalogs.OtherRequirement.Details.Label.DocRequired',
		...sharedProperties,
	},
	SPOtherRequirementDetailsFieldLabelStartDate: {
		defaultValue: 'Start Date',
		description: 'The label for the Start Date field in the other requirements details page.',
		displayPath: `${basePath}Start Date`,
		displayType: 'Label',
		legacyName: 'OtherRequirement/Catalogs.OtherRequirement.Details.Label.StartDate',
		...sharedProperties,
	},
	SPOtherRequirementDetailsFieldLabelEndDate: {
		defaultValue: 'End Date',
		description: 'The label for the End Date field in the other requirements details page.',
		displayPath: `${basePath}End Date`,
		displayType: 'Label',
		legacyName: 'OtherRequirement/Catalogs.OtherRequirement.Details.Label.EndDate',
		...sharedProperties,
	},
	SPOtherRequirementDetailsFieldLabelMaxPoints: {
		defaultValue: 'Max Points',
		description: 'The label for the Max Points field in the other requirements details page.',
		displayPath: `${basePath}Max Points`,
		displayType: 'Label',
		legacyName: 'OtherRequirement/Catalogs.OtherRequirement.Details.Label.MaxPoints',
		...sharedProperties,
	},
	SPOtherRequirementDetailsFieldLabelPoints: {
		defaultValue: 'Points',
		description: 'The label for the Points field in the other requirements details page.',
		displayPath: `${basePath}Points`,
		displayType: 'Label',
		legacyName: 'OtherRequirement/Catalogs.OtherRequirement.Details.Label.Points',
		...sharedProperties,
	},
	SPOtherRequirementDetailsFieldLabelFactorUnits: {
		defaultValue: 'Factor Units',
		description: 'The label for the Factor Units field in the other requirements details page.',
		displayPath: `${basePath}Factor Units`,
		displayType: 'Label',
		legacyName: 'OtherRequirement/Catalogs.OtherRequirement.Details.Label.FactorUnits',
		...sharedProperties,
	},
	SPOtherRequirementDetailsFieldLabelDisplayOrder: {
		defaultValue: 'Display Order',
		description:
			'The label for the Display Order field in the other requirements details page.',
		displayPath: `${basePath}Display Order`,
		displayType: 'Label',
		legacyName: 'OtherRequirement/Catalogs.OtherRequirement.Details.Label.DisplayOrder',
		...sharedProperties,
	},
	SPOtherRequirementDetailsFieldLabelExternalId: {
		defaultValue: 'External ID',
		description: 'The label for the External ID field in the other requirements details page.',
		displayPath: `${basePath}External ID`,
		displayType: 'Label',
		legacyName: 'OtherRequirement/Catalogs.OtherRequirement.Details.Label.ExternalId',
		...sharedProperties,
	},
	SPOtherRequirementDetailsFieldLabelCreateAge: {
		defaultValue: 'Create Age (In Months)',
		description: 'The label for the Create Age field in the other requirements details page.',
		displayPath: `${basePath}Create Age`,
		displayType: 'Label',
		legacyName: 'OtherRequirement/Catalogs.OtherRequirement.Details.Label.CreateAge',
		...sharedProperties,
	},
	SPOtherRequirementDetailsFieldLabelUrl: {
		defaultValue: 'URL',
		description: 'The label for the URL field in the other requirements details page.',
		displayPath: `${basePath}URL`,
		displayType: 'Label',
		legacyName: 'OtherRequirement/Catalogs.OtherRequirement.Details.Label.Url',
		...sharedProperties,
	},
	SPOtherRequirementDetailsFieldLabelSchedulingUrl: {
		defaultValue: 'Scheduling URL',
		description:
			'The label for the Scheduling URL field in the other requirements details page.',
		displayPath: `${basePath}Scheduling URL`,
		displayType: 'Label',
		legacyName: 'OtherRequirement/Catalogs.OtherRequirement.Details.Label.SchedulingUrl',
		...sharedProperties,
	},
	SPOtherRequirementDetailsFieldLabelTranscriptEnabled: {
		defaultValue: 'Transcript Enabled',
		description:
			'The label for the Transcript Enabled field in the other requirements details page.',
		displayPath: `${basePath}Transcript Enabled`,
		displayType: 'Label',
		legacyName: 'OtherRequirement/Catalogs.OtherRequirement.Details.Label.TranscriptEnabled',
		...sharedProperties,
	},
	SPOtherRequirementDetailsFieldLabelProviderRequired: {
		defaultValue: 'Provider Required',
		description:
			'The label for the Provider Required field in the other requirements details page.',
		displayPath: `${basePath}Provider Required`,
		displayType: 'Label',
		legacyName: 'OtherRequirement/Catalogs.OtherRequirement.Details.Label.ProviderRequired',
		...sharedProperties,
	},
	SPOtherRequirementDetailsFieldLabelDescription: {
		defaultValue: 'Description',
		description: 'The label for the Description field in the other requirements details page.',
		displayPath: `${basePath}Description`,
		displayType: 'Label',
		legacyName: 'OtherRequirement/Catalogs.OtherRequirement.Details.Label.Description',
		...sharedProperties,
	},
	SPOtherRequirementDetailsViewLatestAgreement: {
		defaultValue: 'View/Edit Latest Agreement',
		description: 'Button text for viewing/editing the latest agreement.',
		displayPath: `${basePath}View Latest Agreement`,
		displayType: 'Button',
		legacyName: 'OtherRequirement/Catalogs.OtherRequirement.Details.Button.LatestAgreement',
		...sharedProperties,
	},
	SPOtherRequirementDetailsContentValidForever: {
		defaultValue: 'Forever',
		description: 'Text displayed when the valid period is set to forever.',
		displayPath: `${basePath}Valid Forever`,
		displayType: 'Content',
		legacyName: 'OtherRequirement/Catalogs.OtherRequirement.Details.Content.ValidForever',
		...sharedProperties,
	},
	SPOtherRequirementDetailsContentDocNotAllowed: {
		defaultValue: 'Not Allowed',
		description: 'Text displayed when document is not allowed.',
		displayPath: `${basePath}Doc Not Allowed`,
		displayType: 'Content',
		legacyName: 'OtherRequirement/Catalogs.OtherRequirement.Details.Content.DocNotAllowed',
		...sharedProperties,
	},
	SPOtherRequirementDetailsContentDocAllowed: {
		defaultValue: 'Allowed',
		description: 'Text displayed when document is allowed.',
		displayPath: `${basePath}Doc Allowed`,
		displayType: 'Content',
		legacyName: 'OtherRequirement/Catalogs.OtherRequirement.Details.Content.DocAllowed',
		...sharedProperties,
	},
	SPOtherRequirementDetailsContentDocRequired: {
		defaultValue: 'Required',
		description: 'Text displayed when document is required.',
		displayPath: `${basePath}Doc Required`,
		displayType: 'Content',
		legacyName: 'OtherRequirement/Catalogs.OtherRequirement.Details.Content.DocRequired',
		...sharedProperties,
	},
	SPOtherRequirementDetailsContinuingEducationMaxPoints: {
		defaultValue: 'Max Points',
		description: 'Label for the maximum points field in continuing education.',
		displayPath: `${basePath}Max Points`,
		displayType: 'Label',
		legacyName:
			'Credential.ContinuingEducation/Catalogs.OtherRequirements.FieldLabel.MaxPoints',
		...sharedBase,
		feature: HubFeature.CredentialContinuingEducation,
	},
	SPOtherRequirementDetailsContinuingEducationPoints: {
		defaultValue: 'Points',
		description: 'Label for the points field in continuing education.',
		displayPath: `${basePath}Points`,
		displayType: 'Label',
		legacyName: 'Credential.ContinuingEducation/Catalogs.OtherRequirements.FieldLabel.Points',
		...sharedBase,
		feature: HubFeature.CredentialContinuingEducation,
	},
	SPOtherRequirementDetailsContinuingEducationFactorUnits: {
		defaultValue: 'Factor Units',
		description: 'Label for the factor units field in continuing education.',
		displayPath: `${basePath}Factor Units`,
		displayType: 'Label',
		legacyName:
			'Credential.ContinuingEducation/Catalogs.OtherRequirements.FieldLabel.FactorUnits',
		...sharedBase,
		feature: HubFeature.CredentialContinuingEducation,
	},
	SPOtherRequirementDetailsContinuingEducationFactor: {
		defaultValue: 'Factor',
		description: 'Label for the factor field in continuing education.',
		displayPath: `${basePath}Factor`,
		displayType: 'Label',
		legacyName: 'Credential.ContinuingEducation/Catalogs.OtherRequirements.FieldLabel.Factor',
		...sharedBase,
		feature: HubFeature.CredentialContinuingEducation,
	},
	SPOtherRequirementDetailsContentNoLimitText: {
		defaultValue: 'No limit',
		description: 'Text displayed when there is no limit set.',
		displayPath: `${basePath}No Limit Text`,
		displayType: 'Content',
		legacyName: 'OtherRequirement/Catalogs.OtherRequirement.Details.Content.NoLimitText',
		...sharedProperties,
	},
	SPOtherRequirementDetailsContentProviderNotAllowed: {
		defaultValue: 'Not Allowed',
		description: 'Text displayed when provider is not allowed.',
		displayPath: `${basePath}Provider Not Allowed`,
		displayType: 'Content',
		legacyName: 'OtherRequirement/Catalogs.OtherRequirement.Details.Content.ProviderNotAllowed',
		...sharedProperties,
	},
	SPOtherRequirementDetailsContentProviderAllowed: {
		defaultValue: 'Allowed',
		description: 'Text displayed when provider is allowed.',
		displayPath: `${basePath}Provider Allowed`,
		displayType: 'Content',
		legacyName: 'OtherRequirement/Catalogs.OtherRequirement.Details.Content.ProviderAllowed',
		...sharedProperties,
	},
	SPOtherRequirementDetailsContentProviderRequired: {
		defaultValue: 'Required',
		description: 'Text displayed when provider is required.',
		displayPath: `${basePath}Provider Required`,
		displayType: 'Content',
		legacyName: 'OtherRequirement/Catalogs.OtherRequirement.Details.Content.ProviderRequired',
		...sharedProperties,
	},
	SPOtherRequirementDetailsFieldErrorNameRequired: {
		defaultValue: 'This field is required',
		description: 'Error message displayed when the name field is required but empty.',
		displayPath: `${basePath}Name Required`,
		displayType: 'Error',
		legacyName: 'OtherRequirement/Catalogs.OtherRequirement.Details.FieldError.NameRequired',
		...sharedProperties,
	},
	SPOtherRequirementDetailsFieldErrorDisplayOrderInvalid: {
		defaultValue: 'Value must be a positive number',
		description: 'Error message displayed when the display order is invalid.',
		displayPath: `${basePath}Display Order Invalid`,
		displayType: 'Error',
		legacyName:
			'OtherRequirement/Catalogs.OtherRequirement.Details.FieldError.DisplayOrderInvalid',
		...sharedProperties,
	},
	SPOtherRequirementDetailsDateRangeInvalid: {
		defaultValue: 'Invalid date ranges. Start date must be before end date.',
		description: 'Error message displayed when the date range is invalid.',
		displayPath: `${basePath}Date Range Invalid`,
		displayType: 'Error',
		legacyName: 'OtherRequirement/Catalogs.OtherRequirement.Details.DateRangeInvalid',
		...sharedProperties,
	},
	SPOtherRequirementDetailsInformationUrlFieldLabelUrl: {
		defaultValue: 'Url',
		description: 'Label for the URL field in the information section.',
		displayPath: `${basePath}Information URL`,
		displayType: 'Label',
		legacyName:
			'OtherRequirement.InformationUrl/Catalogs.OtherRequirement.Details.FieldLabel.Url',
		...sharedBase,
		feature: HubFeature.OtherRequirementInformationUrl,
	},
	SPOtherRequirementDetailsInformationUrlFieldErrorUrlInvalid: {
		defaultValue: 'Please enter a valid URL, such as https://example.com',
		description: 'Error message displayed when the URL is invalid.',
		displayPath: `${basePath}Information URL Invalid`,
		displayType: 'Error',
		legacyName:
			'OtherRequirement.InformationUrl/Catalogs.OtherRequirement.Details.FieldError.UrlInvalid',
		...sharedBase,
		feature: HubFeature.OtherRequirementInformationUrl,
	},
	SPOtherRequirementDetailsSchedulingUrlFieldLabelSchedulingUrl: {
		defaultValue: 'Scheduling Url',
		description: 'Label for the scheduling URL field.',
		displayPath: `${basePath}Scheduling URL`,
		displayType: 'Label',
		legacyName:
			'OtherRequirement.SchedulingUrl/Catalogs.OtherRequirement.Details.FieldLabel.SchedulingUrl',
		...sharedBase,
		feature: HubFeature.OtherRequirementSchedulingUrl,
	},
	SPOtherRequirementDetailsSchedulingUrlFieldErrorSchedulingUrlInvalid: {
		defaultValue: 'Please enter a valid URL, such as https://example.com',
		description: 'Error message displayed when the scheduling URL is invalid.',
		displayPath: `${basePath}Scheduling URL Invalid`,
		displayType: 'Error',
		legacyName:
			'OtherRequirement.SchedulingUrl/Catalogs.OtherRequirement.Details.FieldError.SchedulingUrlInvalid',
		...sharedBase,
		feature: HubFeature.OtherRequirementSchedulingUrl,
	},
};

export type SPOtherRequirementDetailsLocType = LocMap<SPOtherRequirementDetailsLocNames> &
	LocMap<SPCredentialAgreementLoc>;
